import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { ComponentStatus, StateComponent } from '../../../../../../common/components/state/state.component';
import { ActivatedRoute } from '@angular/router';
import { FormBettingApiService } from '../../../../../../common/apis/form/form-betting-api.service';
import { AnalyticsService } from '../../../../../../common/services/analytics.service';
import { IFormModel } from '../../../../../../common/apis/form/model/form.model';
import { SIDE_OVERLAY_DATA } from '../../../../../../common/components/side-overlay/side-overlay.model';
import { IMatchBetting, IMatchFormConfig, ITeamLookupDto } from '@oogaday/shared';
import { FormBuilder, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatOption } from '@angular/material/core';
import { KeyValuePipe } from '@angular/common';
import { MatSelect } from '@angular/material/select';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { LoadingContainerComponent } from '../../../../../../common/components/loading-container/loading-container.component';
import { SideOverlayWrapperComponent } from '../../../../../../common/components/side-overlay/side-overlay-wrapper/side-overlay-wrapper.component';

@Component({
  selector: 'oo-match-stats',
  templateUrl: './match-stats.component.html',
  styleUrls: ['./match-stats.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SideOverlayWrapperComponent, LoadingContainerComponent, MatFormField, MatLabel, MatSelect, FormsModule, ReactiveFormsModule, MatOption, KeyValuePipe],
})
export class MatchStatsComponent extends StateComponent<MatchStatsComponentState> implements OnInit {

  state: MatchStatsComponentState = {
    status: 'loading',
    matches: [],
    teamsLookup: {},
    teamsFilter: null!,
    filteredMatches: [],
  };
  private readonly route = inject(ActivatedRoute);
  private readonly formApi = inject(FormBettingApiService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly data = inject<MatchStatsComponentData>(SIDE_OVERLAY_DATA);
  private readonly fb = inject(FormBuilder);

  ngOnInit(): void {
    this.analyticsService.trackScreenName('matchStats');
    this.setTeamsFilter();
    this.fetchData();
  }

  private fetchData(): void {
    this.updateStatus('loading');
    this.formApi.getForms(this.data.compGroupId, ['hasBettingResult', 'bettingResult', 'metadata', 'config'])
      .then((forms) => {
        const matches = this.getMatchesToDisplay(forms);
        this.updateState(
          {
            status: 'ready',
            matches,
            teamsLookup: this.createTeamLookup(forms),
            filteredMatches: matches,
          },
        );
      })
      .catch((e: Error) => {
        console.error(e);
        this.updateStatus('error');
      });
  }

  private getMatchesToDisplay(forms: IFormModel<IMatchFormConfig>[]): MatchFormItem[] {
    return forms.filter((form) =>
      form.formType === 'match' &&
      form.hasBettingResult,
    ).map((form) => {
      const homeTeam = form.metadata!.teams
        .find((team: ITeamLookupDto) => team.id === form.config?.homeTeamId)!;
      const awayTeam = form.metadata!.teams
        .find((team: ITeamLookupDto) => team.id === form.config?.awayTeamId)!;

      const teamLookup: { [key: number]: string } = {
        [homeTeam.id]: homeTeam.name,
        [awayTeam.id]: awayTeam.name,
      };

      const bettingResult = form.bettingResult as IMatchBetting;

      return {
        homeTeam: { id: homeTeam.id, name: homeTeam.name },
        awayTeam: { id: awayTeam.id, name: awayTeam.name },
        results: [
          (bettingResult.exactScore ? ['Score', bettingResult.exactScore[0] + ' - ' + bettingResult.exactScore[1]] : null),
          (bettingResult.numberOfCards ? ['Number of cards', bettingResult.numberOfCards + ''] : null),
          (bettingResult.firstTeamToScore ?
            ['First team to score', bettingResult.firstTeamToScore !== 'none' ? teamLookup[bettingResult.firstTeamToScore] : 'None'] : null),
          (bettingResult.firstTeamToGetCard ?
            ['First team to get a card', bettingResult.firstTeamToGetCard !== 'none' ? teamLookup[bettingResult.firstTeamToGetCard] : 'None'] : null),
          (bettingResult.firstTeamToHaveCorner ?
            ['First team to have a corner', bettingResult.firstTeamToHaveCorner !== 'none' ? teamLookup[bettingResult.firstTeamToHaveCorner] : 'None'] : null),
        ].filter((r) => r !== null) as [string, string][],
      };
    });
  }

  private createTeamLookup(forms: IFormModel[]): TeamsLookup {
    return forms
      .filter((form) =>
        form.formType === 'match' &&
        form.hasBettingResult)
      .flatMap((form) => form.metadata!.teams as ITeamLookupDto[])
      .reduce((acc: TeamsLookup, team) => {
        if (!acc[team.id]) {
          acc[team.id] = team;
        }

        return acc;
      }, {});
  }

  private setTeamsFilter(): void {
    this.updateState({
      teamsFilter: this.fb.nonNullable.control([]),
    });

    this.addSubscription(
      this.state.teamsFilter.valueChanges.subscribe((value) => {
        const idsSet = new Set(value);
        this.updateState({
          filteredMatches: !idsSet.size ? this.state.matches : this.state.matches
            .filter((match) =>
              idsSet.has(match.homeTeam.id) || idsSet.has(match.awayTeam.id)),
        });
      }),
    );
  }
}

interface MatchStatsComponentState {
  status: ComponentStatus;
  matches: MatchFormItem[];
  teamsLookup: TeamsLookup;
  teamsFilter: FormControl<number[]>;
  filteredMatches: MatchFormItem[];
}

export interface MatchStatsComponentData {
  compGroupId: string;
}

interface MatchFormItem {
  homeTeam: FromTeamItem;
  awayTeam: FromTeamItem;
  results: ([string, string])[];
}

interface FromTeamItem {
  id: number;
  name: string;
}

type TeamsLookup = { [key: number]: ITeamLookupDto };
